import classNames from 'classnames';
import React from 'react';
import isEqual from 'react-fast-compare';

import Timestamp from 'notebook/mediocre/core-ui/Timestamp';
import {EditorProps, MediocreItemEditor} from 'notebook/mediocre/EditorTypes';
import {MediocreSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import convertNumbersToWords from 'notebook/mediocre/util/convertNumbersToWords';
import {NotePart} from 'notebook/util/NoteUtilConstants';

import MediocreItemComponent from './MediocreItemComponent';

export interface MediocreItemProps extends MediocreSearchResultProps {
  item: NotePart;
  index: number;
  isHighlighted: boolean;
  isSelected: boolean;
  isBeforeInsert: boolean;
  selectedSubIndex?: string;

  // This component has a strange interface: we are passing the MediocreEditor instance to this (child)
  // component, which allows us to access editor methods and thread them to the child components that
  // need to call them.
  // Note: we should never access props / state / directly call non-pure methods of `editor` inside this
  //       component's render. The editor has a collection of pure methods that are all prefixed with 'get'
  //       to access information about the rendered item, e.g. getCid, getCreator.
  // [Tech Debt]: All of the methods should be moved onto `editorProps`
  editor: MediocreItemEditor<NotePart>;

  // These are props that are derived from the editor's props/state that will only be threaded to the
  // `make` method of this item's corresponding Mediocre plugin. This is computed on-the-fly per item so
  // that other items do not render unnecessarily. It is deep equal checked as it is computed every render.
  // A plugin defines an (optional) `getItemProps` method that gets called when constructing the React
  // Element for this MediocreItem.
  // [Tech Debt]: Kill this off in favor of the underlying component directly accessing the necessary
  //              information via a store.
  itemProps: unknown;

  // We consolidate all of the props of the editor that need to be univerally accessible by all items,
  // e.g. fileId, locked. Ideally all of the methods that are being accessed via `editor` are migrated to
  // being present on this `editorProps` object. This is expected to rarely change, and MediocreEditor
  // is responsible for ensuring that a new object is only constructed when one of the underlying props
  // changes (see: `makeEditorProps`).
  editorProps: EditorProps<NotePart> & {
    showTimestamps?: boolean;
    getType: (item: NotePart) => string;
  };
}

/**
 * A light-weight component that wraps an item component in a MediocreEditor, registering mouse handlers for
 * click-and-drag selections.
 *
 * It ensures performant rendering by not taking in a React element as props.children as this is almost
 * guaranteed to be re-created per render. Instead, we access plugins' make methods via the passed in
 * `editor` object, which are *mostly* pure methods, except for usages of threading editor methods to
 * components which are presumed to be non-changing methods that are never called on render.
 *
 * It optionally renders timestamp to the left of the wrapped component.
 */
class MediocreItem extends React.Component<MediocreItemProps> {
  /**
   * This is a very hot code path, which gets run once per MediocreItem for every keystroke. This method
   * is functionally equivalent to PureRenderMixin's shouldComponentUpdate except we do a deep equals
   * check for itemProps.
   */
  static havePropsChanged(prevProps: MediocreItemProps, nextProps: MediocreItemProps): boolean {
    return (
      prevProps.item !== nextProps.item ||
      prevProps.index !== nextProps.index ||
      prevProps.isHighlighted !== nextProps.isHighlighted ||
      prevProps.isSelected !== nextProps.isSelected ||
      prevProps.isBeforeInsert !== nextProps.isBeforeInsert ||
      prevProps.selectedSubIndex !== nextProps.selectedSubIndex ||
      prevProps.editor !== nextProps.editor ||
      prevProps.editorProps !== nextProps.editorProps ||
      prevProps.searchResultsTree !== nextProps.searchResultsTree ||
      prevProps.selectedSearchResultTree !== nextProps.selectedSearchResultTree ||
      !isEqual(prevProps.itemProps, nextProps.itemProps)
    );
  }

  // Declared only for sinon to stub in tests
  override componentDidMount(): void {}

  override shouldComponentUpdate(nextProps: MediocreItemProps): boolean {
    return MediocreItem.havePropsChanged(this.props, nextProps);
  }

  _handleMouseDown = (e: React.MouseEvent<HTMLElement>): void => {
    this.props.editor.itemMouseDownHandler(this.props.index, e);
  };

  _handleMouseOver = (e: React.MouseEvent<HTMLElement>): void => {
    this.props.editor.itemMouseOverHandler(this.props.index, e);
  };

  _handleMouseMove = (e: React.MouseEvent<HTMLElement>): void => {
    this.props.editor.itemMouseMoveHandler(this.props.index, e);
  };

  override render(): JSX.Element {
    const type = this.props.editorProps.getType(this.props.item);
    const plugin = this.props.editor.getPluginByType(type);
    const itemComponent = <MediocreItemComponent {...this.props} />;
    return (
      <div
        // Datadog uses this id to resolve unqiue selectors for click events.
        // They will only use the id if it does not contain any numbers.
        // Bug: https://github.com/DataDog/browser-sdk/issues/2751
        // TODO(BNCH-96065): We should remove convertNumbersToWords when the bug is fixed or there is a
        // better solution.
        id={`mediocre-item-${convertNumbersToWords(this.props.item.cid)}`}
        data-scroll-id={this.props.item.cid}
        data-selected-subindex={this.props.selectedSubIndex}
        className={classNames('mediocre-item', plugin.typeClass, {
          selected: this.props.isSelected,
          'is-highlighted': this.props.isHighlighted,
          'is-before-insert': this.props.isBeforeInsert,
          'is-important': this.props.item.important,
          'mediocre-item--withTimestamp': this.props.editorProps.showTimestamps,
        })}
        onMouseDown={this._handleMouseDown}
        onMouseOver={this._handleMouseOver}
        onMouseMove={this._handleMouseMove}
      >
        {!this.props.editorProps.showTimestamps ? (
          itemComponent
        ) : (
          <div className="mediocre-row">
            <Timestamp
              creator={this.props.editor.getCreator!(this.props.item)}
              isodate={this.props.editor.getModifiedAt!(this.props.item)}
            />
            <div className="mediocre-row-item">{itemComponent}</div>
          </div>
        )}
      </div>
    );
  }
}

export default MediocreItem;
